import type { Dispatch } from 'react';
import { useState } from 'react';
import type { Instance } from 'tippy.js';

interface UseTippy {
  set: Dispatch<Instance | null>;
  toggle: () => void;
  close: () => void;
  open: () => void;
  state: Instance['state'] | undefined;
  /**
   * Focus trigger reference element.
   */
  focusTriggerElement: () => void;
}

/**
 * Hook to get and handle a Tippy instance.
 *
 * **WARNING!** _This hook is for internal use in the Design System. If you
 * need to use it for a custom component, please discuss your use case with
 * the Design System squad before proceeding, as they may be able to help
 * you with a better solution._
 * @private
 * @see Popover
 * @see Dropdown
 * @see MultiSelect
 */
export function useTippy(): UseTippy {
  const [instance, setInstance] = useState<Instance | null>(null);

  const toggle = () => {
    if (instance?.state.isVisible) {
      instance?.hide();
    } else {
      instance?.show();
    }
  };

  const focusTriggerElement: UseTippy['focusTriggerElement'] = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore focus actually exists in instance.reference
    instance?.reference.focus();
  };

  const close = instance?.hide;
  const open = instance?.show;

  return {
    set: setInstance,
    toggle,
    close,
    open,
    state: instance?.state,
    focusTriggerElement,
  };
}
